import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Loader2, Play, Power } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { scheduleText } from "@/components/settings/scheduled-jobs/jobMeta";
import { errMsg } from "../ui";
import { fmtTime } from "../preview/meta";

function Confirm({ open, setOpen, title, text, label, onGo, testid }) {
  const [ok, setOk] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); setOk(false); }}>
      <AlertDialogContent data-testid={`${testid}-dialog`}>
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{text}</AlertDialogDescription></AlertDialogHeader>
        <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid={`${testid}-confirm`} />
          I understand this changes live Stax Bill subscriptions.</label>
        <AlertDialogFooter><AlertDialogCancel data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!ok} onClick={(e) => { e.preventDefault(); onGo(); setOpen(false); }} data-testid={`${testid}-go`}>{label}</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** On/off (super admins), schedule, next run and Run now. */
export function SyncStatusCard({ d, reload }) {
  const [ask, setAsk] = useState("");
  const [busy, setBusy] = useState(false);
  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e, "Could not do that")); } finally { setBusy(false); }
  };
  const on = () => call(() => api.put("/billing-sync/autosync/enabled", { enabled: true, confirm: true }), "Tracked Items Sync is ON");
  const off = () => call(() => api.put("/billing-sync/autosync/enabled", { enabled: false }), "Tracked Items Sync switched off");
  const run = () => call(() => api.post("/billing-sync/autosync/run", { confirm: true }), "Tracked Items Sync started — results show below");
  const tz = d.tz === "UTC" ? "UTC" : "Mountain";
  return (
    <Card className="p-5" data-testid="tis-status">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Automatic sync</div>
      <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${d.enabled ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`} data-testid="tis-state">
        <Power className="h-3.5 w-3.5" /> {d.enabled ? "ON — applies changes every day" : "Off — nothing runs on its own"}</div>
      {d.changed_by && <div className="mt-1 text-[11px] text-slate-500">Last changed by {d.changed_by} · {fmtTime(d.changed_at)}</div>}
      <div className="mt-3 flex items-center gap-2 text-sm text-slate-700" data-testid="tis-schedule">
        <CalendarClock className="h-4 w-4 text-slate-400" /> {scheduleText(d.schedule)} {tz}
        {d.next_run && <span className="text-xs text-slate-500">· next {fmtTime(d.next_run)} your time</span>}</div>
      <Link to="/settings?tab=platform" className="mt-1 block text-xs text-emerald-700 hover:underline" data-testid="tis-schedule-link">Change the time in Settings → Platform → Scheduled jobs</Link>
      {d.is_super ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {d.enabled
            ? <Button size="sm" variant="outline" disabled={busy} onClick={off} data-testid="tis-off">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Switch off</Button>
            : <Button size="sm" disabled={busy} onClick={() => setAsk("on")} data-testid="tis-on">Switch on…</Button>}
          <Button size="sm" variant="outline" disabled={busy || d.running} onClick={() => setAsk("run")} data-testid="tis-run-now">
            {d.running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} {d.running ? "Running…" : "Run now…"}</Button>
        </div>
      ) : <p className="mt-4 text-[11px] text-slate-500">Only super admins can switch it on or run it.</p>}
      <Confirm open={ask === "on"} setOpen={(v) => setAsk(v ? "on" : "")} testid="tis-on" label="Switch on" onGo={on}
        title="Apply tracked-item changes to Stax Bill every day?"
        text={`From the next ${scheduleText(d.schedule).toLowerCase()} ${tz}, every add, move, removal and new subscription the preview finds is written to Stax Bill without anyone ticking rows.`} />
      <Confirm open={ask === "run"} setOpen={(v) => setAsk(v ? "run" : "")} testid="tis-run" label="Run it now" onGo={run}
        title="Run the Tracked Items Sync now?"
        text="A full preview of every platform runs (about 1–2 minutes), then everything it lists is applied to Stax Bill straight away." />
    </Card>
  );
}
